import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    Menu,
    Paper,
    Tab,
    Tabs,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, includes, toLower } from "lodash";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import FormTitle from "../../../common/js/components/FormTitle";
import Gallery from "../../../common/js/components/Gallery";
import Snackbar from "../../../common/js/components/KMSnackbar";
import KMSwitch from "../../../common/js/components/KMSwitch";
import LeaseAndSaleDropdownOptions from "../../../common/js/components/LeaseAndSaleDropdownOptions";
import { Add, Comments, LeftChevron } from "../../../common/js/icons";
import CompsTable from "../comp/CompsTableContainer";
import DocumentDrop from "../documents/DocumentDropContainer";
import ListingDataPreview from "../listing/ListingDataPreview";
import RequestModalContainer from "../listing/RequestModalContainer";
import HealthcareForm from "../property/HealthcareForm";
import ResearchTab from "../research/ResearchTab";
import { us_states_options } from "../utils/constants";
import { buildAddress, filterActiveListings, filterByQuery, toDollarAmount, toSqft } from "../utils/operations";
import AssetServicesDeleteModalContainer from "./AssetServicesDeleteModalContainer";
import AssetServicesSnapshotModalContainer from "./AssetServicesSnapshotModalContainer";
import BuildingForm from "./BuildingForm";
import { compColumnsTofilter, getSpecialtyTypeDisplayName, listingColumnsTofilter } from "./constants";
import HospitalityForm from "./HospitalityForm";
import IndustrialForm from "./IndustrialForm";
import LandForm from "./LandForm";
import MultifamilyForm from "./MultifamilyForm";
import OfficeForm from "./OfficeForm";
import OtherForm from "./OtherForm";
import ParkingInfoForm from "./ParkingInfoForm";
import AssetServicesTab from "./PropertyAssetServicesTab";
import RetailForm from "./RetailForm";
import SaveAndSyncContainer from "./SaveAndSyncContainer";

const StyledPageToolbar = styled("div")(({ theme }) => ({
    margin: "20px -4px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    width: "100%",
}));

const StyledPaperPadding = styled(Paper)({
    padding: "5px 20px 20px",
    marginBottom: "24px",
});

const StyledHeading = styled("h3")({
    fontSize: "18px",
    margin: "14px 0",
});

const StyledHr = styled("hr")({
    margin: "0 -20px 20px -20px",
});

const StyledDefTerm = styled("span")({
    fontSize: "13px",
    textTransform: "uppercase",
    color: "#9E9E9E",
    fontWeight: 600,
    lineHeight: "26px",
    whiteSpace: "nowrap",
});

const StyledDefData = styled("span")({
    fontSize: "14px",
    color: "#000",
});

const StyledTabOne = styled(Tab)({
    marginRight: "20px",
});

const StyledAlternateContent = styled("div")({
    margin: "auto",
    textAlign: "center",
    marginTop: "3em",
    marginBottom: "3em",
    color: "rgba(0, 0, 0, 0.30)",
    fontSize: "20px",
});

const StyledMoveIt = styled(Button)({
    marginRight: "20px",
    padding: 0,
    "&:hover": {
        background: "transparent",
    },
});

const StyledLightButton = styled(Button)({
    fontWeight: "400",
});

const StyledLightButtonIcon = styled("span")({
    paddingRight: "3px",
    paddingTop: "6px",
    marginRight: "3px",
});

const StyledAssetServicesButtonsContainer = styled("div")({
    display: "flex",
    alignItems: "center",
});

const StyledFlexRow = styled("div")({
    display: "flex",
    flexWrap: "wrap",
    gap: "2px",
    margin: "-4px",
});

const StyledGeneralInfoItem = styled("div")({
    flexGrow: 1,
    flexBasis: 0,
    maxWidth: "100%",
    padding: "4px",
    boxSizing: "border-box",
});

const StyledPropertyInformation = styled("div")({
    flex: "1 1 0",
});

const StyledFlexContainer = styled("div")({
    display: "flex",
    gap: "1vw",
});

const StyledPropertyInfoFlexHalf = styled("div")({
    maxWidth: "50%",
});

const StyledDivider = styled(Divider)({
    marginBottom: "1vh",
});

const PropertyDetail = (props) => {
    const {
        addListingGivenProperty,
        clearErrorByField,
        closeDeal,
        compHistory,
        elementsSaving,
        errors,
        getFlyer,
        handleInput,
        history,
        images,
        isLoading,
        listings,
        navigateToRequestPropertyUpdate,
        navigateToRequestUpdate,
        property,
        propertyKey,
        propertyManagement,
        propertyType,
        role,
        switchTab,
        tab,
        toggleRequestModal,
    } = props;

    const [closeDealDialogOpen, setCloseDealDialogOpen] = useState(false);
    const [inactiveListingsOnly, setInactiveListingsOnly] = useState(false);
    const [filterQuery] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState(false);
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [listingToClose, setListingToClose] = useState(null);
    const [addListingOpen, setAddListingOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(undefined);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleClickOpen = (listing) => {
        setCloseDealDialogOpen(true);
        setListingToClose(listing);
    };

    const handleClose = async () => {
        const result = await closeDeal(listingToClose);
        if (result) {
            if (result.dealKey) {
                history.push(`/deal/${result.dealKey}`);
            }
        }
        setCloseDealDialogOpen(false);
        setListingToClose(null);
    };

    const handleAddListingMenuOpen = (anchorEl) => {
        setAddListingOpen(true);
        setAnchorEl(anchorEl);
    };

    const handleAddListingMenuClose = () => {
        setAddListingOpen(false);
    };

    const handlePropertyRequestUpdate = () => {
        const propertyKey = get(property, "propertyKey");
        navigateToRequestPropertyUpdate(propertyKey, `/propdetail/${propertyKey}`);
        history.push(`/property/${propertyKey}`);
    };

    const toggleInactiveListingsOnly = () => {
        setInactiveListingsOnly(!inactiveListingsOnly);
    };

    const handleEditResearchSnapshot = (result) => {
        if (result === true) {
            setSnackbarMessage("Success: Your edits have been applied");
            setSnackbarVariant("success");
            setSnackbarOpen(true);
        } else if (result === false) {
            setSnackbarMessage("Failed: Your edit was not processed.");
            setSnackbarVariant("error");
            setSnackbarOpen(true);
        }
    };

    const navigateToEditProperty = () => {
        const propertyKey = get(property, "propertyKey");
        history.push(`/property/${propertyKey}`);
    };

    const filteredListings =
        listings && listings instanceof Array
            ? listings
                  .filter((k) => filterActiveListings(k, inactiveListingsOnly))
                  .filter((x) => filterByQuery(x, listingColumnsTofilter, filterQuery))
            : listings;

    const filteredComps =
        filterQuery && compHistory
            ? compHistory.filter((x) => filterByQuery(x, compColumnsTofilter, filterQuery))
            : compHistory;

    return (
        <React.Fragment>
            <Snackbar
                open={snackbarOpen}
                message={snackbarMessage}
                variant={snackbarVariant}
                vertical="top"
                horizontal="center"
                autoHideDuration={3000}
                onClose={() => {
                    setSnackbarOpen(false);
                }}
            />
            <StyledPageToolbar>
                <div style={{ minWidth: 0, padding: "4px" }}>
                    <Link to="/properties">
                        <LeftChevron />
                    </Link>
                </div>
                <div style={{ flex: 1, padding: "4px" }}>
                    <h2 className="page-title">{get(property, "propertyAttributes.propertyName", "")}</h2>
                    <p className="page-subtitle">{buildAddress(get(property, "primaryAddress", {}))}</p>
                </div>
                <div style={{ flex: 1, textAlign: "right", padding: "4px" }}>
                    <h2 className="page-title text-right">
                        {`${toDollarAmount(get(property, "propertyAttributes.avgAnnualPsfRate", 0))} ${
                            get(property, "propertyAttributes.avgAnnualPsfRate", null) ? "-" : ""
                        } ${toDollarAmount(get(property, "propertyAttributes.blendedRate", 0))} USD/SF/YR`}
                    </h2>
                    <p className="page-subtitle text-right">{`${toSqft(
                        get(property, "propertyAttributes.totalBldgSf", 0)
                    )} | ${get(property, "propertyType", 0)}`}</p>
                </div>
            </StyledPageToolbar>
            <div style={{ display: "flex", width: "100%", gap: "16px" }}>
                <div style={{ flex: "0 0 40%", maxWidth: "40%" }}>
                    <Gallery
                        propertyKey={propertyKey}
                        images={Object.keys(images).map((key) => {
                            return images[key].url;
                        })}
                    ></Gallery>
                </div>
                <div style={{ flex: "1 1 60%", maxWidth: "60%" }}>
                    <StyledPaperPadding>
                        <StyledHeading>General Information</StyledHeading>
                        <StyledHr />
                        {isLoading === false && (
                            <React.Fragment>
                                <StyledFlexRow>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>Property ID</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefData>{property.propertyKey}</StyledDefData>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>Leed Certification</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefData>
                                            {get(property, "propertyAttributes.leedCertFlg", false) === true
                                                ? property.propertyAttributes.leedCertLevel
                                                : "None"}
                                        </StyledDefData>
                                    </StyledGeneralInfoItem>
                                </StyledFlexRow>
                                <StyledFlexRow>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>Property SF</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefData>
                                            {toSqft(get(property, "propertyAttributes.totalBldgSf", {}))}
                                        </StyledDefData>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>Property Type</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefData>{property.propertyType}</StyledDefData>
                                    </StyledGeneralInfoItem>
                                </StyledFlexRow>
                                <StyledFlexRow>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>KM Managed SF</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefData>
                                            {toSqft(get(propertyManagement, "grossSqFt", {}))}
                                        </StyledDefData>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>Property Use</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefData>
                                            {getSpecialtyTypeDisplayName(
                                                get(property, "propertyType"),
                                                get(property, "propertyAttributes.specialtyType", "-")
                                            )}
                                        </StyledDefData>
                                    </StyledGeneralInfoItem>
                                </StyledFlexRow>
                                <StyledFlexRow>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>Unit Count</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefData>
                                            {get(property, "propertyAttributes.totalUnits", "-")}
                                        </StyledDefData>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>Condo Units</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefData>
                                            {get(property, "propertyAttributes.condoFlg", false) === true
                                                ? "Yes"
                                                : "No"}
                                        </StyledDefData>
                                    </StyledGeneralInfoItem>
                                </StyledFlexRow>
                                <StyledFlexRow>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>Room Count</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>
                                            {get(property, "propertyAttributes.roomCount", "-")}
                                        </StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>Market Area</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>
                                            {get(property, "propertyAttributes.marketArea", "-")}
                                        </StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                </StyledFlexRow>
                                <StyledFlexRow>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm># of Floors</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>
                                            {get(property, "propertyAttributes.numFloors", "-")}
                                        </StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>Submarket Area</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefData>
                                            {get(property, "propertyAttributes.submarketArea", "-")}
                                        </StyledDefData>
                                    </StyledGeneralInfoItem>
                                </StyledFlexRow>
                                <StyledFlexRow>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>PM Client Property ID</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefData>
                                            {get(propertyManagement, "accountingSoftwareId", "")}
                                        </StyledDefData>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>Micro Market</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefData>
                                            {get(property, "propertyAttributes.micromarketArea", "-")}
                                        </StyledDefData>
                                    </StyledGeneralInfoItem>
                                </StyledFlexRow>
                                <StyledFlexRow>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>Property Manager</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefData>{get(property, "propertyManagerName", "")}</StyledDefData>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm></StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm></StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                </StyledFlexRow>
                                <StyledFlexRow>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm>Appraiser</StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm></StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm></StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                    <StyledGeneralInfoItem>
                                        <StyledDefTerm></StyledDefTerm>
                                    </StyledGeneralInfoItem>
                                </StyledFlexRow>
                            </React.Fragment>
                        )}
                    </StyledPaperPadding>
                </div>
            </div>
            <StyledPaperPadding>
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        width: "100%",
                        boxSizing: "border-box",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <div>
                        <Tabs value={tab} onChange={switchTab}>
                            <StyledTabOne value="property" label="Property" />
                            <Tab value="property_listings" label="Listings" />
                            <Tab value="comp_history_for_property" label="Comp History" />
                            {includes(
                                ["propertyManagement", "transition", "accounting", "dataEntry", "dataAdmin"],
                                role
                            ) && <Tab value="property_management" label="Asset Services" />}
                            {includes(["research", "researchAdmin", "dataAdmin", "dataEntry"], role) && (
                                <Tab value="research_tab" label="Research" />
                            )}
                        </Tabs>
                    </div>
                    {tab === "property_management" &&
                        propertyManagement &&
                        includes(["transition", "dataEntry", "dataAdmin"], role) && (
                            <StyledAssetServicesButtonsContainer>
                                <AssetServicesDeleteModalContainer propertyKey={propertyKey} />
                                <AssetServicesSnapshotModalContainer
                                    propertyKey={propertyKey}
                                    errors={errors}
                                    clearErrorByField={clearErrorByField}
                                    saving={elementsSaving}
                                />
                                <SaveAndSyncContainer />
                            </StyledAssetServicesButtonsContainer>
                        )}
                </div>

                <StyledDivider />

                {/*** Listings ***/}

                {tab === "property_listings" && (
                    <React.Fragment>
                        <div
                            style={{
                                justifyContent: "space-between",
                                display: "flex",
                                flexWrap: "wrap",
                                boxSizing: "border-box",
                                width: "100%",
                            }}
                        >
                            <div style={{ boxSizing: "border-box" }}>
                                <StyledHeading style={{ marginBottom: "2px" }}>Listings</StyledHeading>
                                <div className="page-subtitle" style={{ marginBottom: "16px" }}>
                                    Showing{" "}
                                    {`${filteredListings.length} ${
                                        filteredListings.length === 1 ? "listing" : "listings"
                                    }`}
                                </div>
                            </div>
                            <div style={{ boxSizing: "border-box" }}>
                                <div
                                    style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        boxSizing: "border-box",
                                        margin: "-4px",
                                    }}
                                >
                                    <div style={{ padding: "4px" }}>
                                        <FormControlLabel
                                            label={`Inactive Listings  `}
                                            labelPlacement="start"
                                            style={{ gap: "20px" }}
                                            control={
                                                <KMSwitch
                                                    checked={inactiveListingsOnly}
                                                    onChange={toggleInactiveListingsOnly}
                                                    value={inactiveListingsOnly}
                                                />
                                            }
                                        />
                                        <RequestModalContainer
                                            {...props}
                                            toggleRequestModal={toggleRequestModal}
                                            modalType="listing"
                                        />
                                    </div>
                                    <div style={{ padding: "4px" }}>
                                        <StyledMoveIt
                                            color="primary"
                                            onClick={(e) => handleAddListingMenuOpen(e.currentTarget)}
                                        >
                                            <Add fill="#4b94c1" />
                                            Add Listing
                                        </StyledMoveIt>
                                        <Menu
                                            id="long-menu"
                                            anchorEl={anchorEl}
                                            open={addListingOpen}
                                            onClose={handleAddListingMenuClose}
                                            slotProps={{
                                                paper: {
                                                    style: {
                                                        width: 200,
                                                    },
                                                },
                                            }}
                                        >
                                            <LeaseAndSaleDropdownOptions
                                                callback={addListingGivenProperty}
                                                property={property}
                                            />
                                        </Menu>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {elementsSaving.property_listings ? (
                            <StyledAlternateContent>
                                <CircularProgress color="primary" />
                            </StyledAlternateContent>
                        ) : filteredListings && filteredListings.length > 0 ? (
                            <React.Fragment>
                                {filteredListings.map((listing, j) => (
                                    <ListingDataPreview
                                        listing={listing}
                                        history={history}
                                        openCloseDealDialog={handleClickOpen}
                                        getFlyer={getFlyer}
                                        navigateToRequestUpdate={navigateToRequestUpdate}
                                        currentPath={`/propdetail/${get(property, "propertyKey", null)}`}
                                        canClose={includes(
                                            ["broker", "portlandBrokerAdmin", "arizonaBrokerAdmin", "evp"],
                                            role
                                        )}
                                    />
                                ))}
                            </React.Fragment>
                        ) : (
                            <StyledAlternateContent>No listings</StyledAlternateContent>
                        )}
                    </React.Fragment>
                )}

                {/*** Comp history ***/}

                {tab === "comp_history_for_property" && (
                    <React.Fragment>
                        <StyledHeading style={{ margin: "14px 0 2px 0" }}>Comps</StyledHeading>
                        <div className="page-subtitle">
                            Showing {`${filteredComps.length} ${filteredComps.length === 1 ? "comp" : "comps"}`}
                        </div>

                        {elementsSaving.comp_history_for_property ? (
                            <StyledAlternateContent>
                                <CircularProgress color="primary" />
                            </StyledAlternateContent>
                        ) : compHistory && compHistory.length > 0 ? (
                            <CompsTable comps={filteredComps} compTab="comp_history_for_property" />
                        ) : (
                            <StyledAlternateContent>Nothing to Show</StyledAlternateContent>
                        )}
                    </React.Fragment>
                )}

                {/*** Property Management ***/}

                <AssetServicesTab {...props} />

                {/*** Property Information ***/}

                {tab === "property" && (
                    <React.Fragment>
                        <StyledFlexRow>
                            <div style={{ flex: 1, textAlign: "left", padding: "4px" }}>
                                <h3>Property Information</h3>
                            </div>
                            <div style={{ flex: 1, textAlign: "right", padding: "4px" }}>
                                {includes(["dataEntry", "dataAdmin"], role) && (
                                    <Button
                                        onClick={() => navigateToEditProperty(get(property, "propertyKey", null))}
                                        variant="contained"
                                        color="primary"
                                        style={{ marginRight: "10px" }}
                                    >
                                        Edit
                                    </Button>
                                )}
                                {!includes(["dataEntry", "dataAdmin"], role) && (
                                    <StyledLightButton
                                        onClick={handlePropertyRequestUpdate}
                                        color="primary"
                                        style={{ marginRight: "10px" }}
                                    >
                                        <StyledLightButtonIcon>
                                            <Comments fill="#2C99D2" />
                                        </StyledLightButtonIcon>
                                        Request Update
                                    </StyledLightButton>
                                )}
                            </div>
                        </StyledFlexRow>
                        <StyledFlexContainer>
                            <StyledPropertyInformation>
                                <BSInput
                                    id="propertyName"
                                    label="Property Name"
                                    placeholder="Enter property name"
                                    value={get(property, "propertyAttributes.propertyName", "")}
                                    disabled
                                />
                                <BSInput
                                    id="address1"
                                    label="Address"
                                    placeholder="Enter address"
                                    value={get(property, "primaryAddress.address1", "")}
                                    disabled
                                />
                                <StyledFlexContainer>
                                    <BSInput
                                        id="city"
                                        label="City"
                                        placeholder="Enter city"
                                        value={get(property, "primaryAddress.city.city", "")}
                                        disabled
                                        extraFormControlStyles={{ flex: "1 1 0" }}
                                    />

                                    <BSSelect
                                        id="state"
                                        label="State"
                                        placeholder="Select state"
                                        value={get(property, "primaryAddress.city.state.stateCode", "")}
                                        disabled
                                        options={us_states_options}
                                        extraFormControlStyles={{ flex: "1 1 0" }}
                                    />
                                    <BSInput
                                        id="zip"
                                        label="Zip"
                                        placeholder="Enter zip code"
                                        value={get(property, "primaryAddress.zipCode.zipCode", "")}
                                        disabled
                                        extraFormControlStyles={{ flex: "1 1 0" }}
                                    />
                                </StyledFlexContainer>
                                <BSInput
                                    id="propertyCounty"
                                    label="Property County"
                                    placeholder="Select County"
                                    value={get(property, "primaryAddress.county.name", "")}
                                    disabled
                                />
                                <StyledPropertyInfoFlexHalf>
                                    <BSInput
                                        id="address2"
                                        label="2nd Address"
                                        placeholder="Enter address"
                                        value={get(property, "secondaryAddress", "")}
                                        disabled
                                    />
                                </StyledPropertyInfoFlexHalf>
                            </StyledPropertyInformation>
                            <StyledPropertyInformation>
                                <StyledFlexContainer>
                                    <BSInput
                                        id="propertyType"
                                        label="Property Type"
                                        placeholder="Select type"
                                        value={get(property, "propertyType", "")}
                                        disabled
                                        extraFormControlStyles={{ flex: "1 1 0" }}
                                    />
                                    <BSInput
                                        id="useType"
                                        label="Property Use"
                                        placeholder="Select use"
                                        value={getSpecialtyTypeDisplayName(
                                            get(property, "propertyType"),
                                            get(property, "propertyAttributes.specialtyType")
                                        )}
                                        disabled
                                        extraFormControlStyles={{ flex: "1 1 0" }}
                                    />
                                </StyledFlexContainer>
                                <StyledFlexContainer>
                                    <BSInput
                                        id="marketArea"
                                        label="Market Area"
                                        value={get(property, "propertyAttributes.marketArea", "")}
                                        disabled
                                        extraFormControlStyles={{ flex: "1 1 0" }}
                                    />
                                    <BSInput
                                        id="submarketArea"
                                        label="Submarket"
                                        value={get(property, "propertyAttributes.submarketArea", "")}
                                        disabled
                                        extraFormControlStyles={{ flex: "1 1 0" }}
                                    />
                                    <BSInput
                                        id="micromarketArea"
                                        label="Micro Market"
                                        value={get(property, "propertyAttributes.micromarketArea", "")}
                                        disabled
                                        extraFormControlStyles={{ flex: "1 1 0" }}
                                    />
                                </StyledFlexContainer>
                                {(property?.propertyAttributes?.parcels ?? []).map((parcel) => (
                                    <React.Fragment>
                                        <StyledFlexContainer>
                                            <BSInput
                                                id="buildingName0"
                                                label="Latitude"
                                                value={get(parcel, "latitude", "")}
                                                placeholder="Enter Latitude"
                                                disabled
                                                extraFormControlStyles={{ flex: "1 1 0" }}
                                            />
                                            <BSInput
                                                id="buildingName0"
                                                label="Longitude"
                                                value={get(parcel, "longitude", "")}
                                                placeholder="Enter Longitude"
                                                disabled
                                                extraFormControlStyles={{ flex: "1 1 0" }}
                                            />
                                        </StyledFlexContainer>
                                        <BSInput
                                            id="buildingName0"
                                            label="APN/Parcel Code/Parcel ID"
                                            value={get(parcel, "apn", "")}
                                            placeholder="Enter Parcel ID"
                                            disabled
                                        />
                                    </React.Fragment>
                                ))}
                            </StyledPropertyInformation>
                        </StyledFlexContainer>

                        <Divider />

                        <FormTitle title="Building Information" />
                        <BuildingForm
                            {...props}
                            data={get(property, "propertyAttributes", {})}
                            context="property"
                            errors={{}}
                            disableInputs={true}
                            onChange={(field, value) => handleInput("property.propertyAttributes." + field, value)}
                        />

                        <Divider />

                        <FormTitle title="Parking Information" />
                        <ParkingInfoForm
                            {...props}
                            data={get(property, "propertyAttributes", {})}
                            context="property"
                            errors={{}}
                            disableInputs={true}
                            onChange={(field, value) => handleInput("property.propertyAttributes." + field, value)}
                        />
                        {toLower(propertyType) === "office" && (
                            <React.Fragment>
                                <Divider />
                                <FormTitle title="Office Information" />
                                <OfficeForm
                                    {...props}
                                    data={get(property, "propertyAttributes", {})}
                                    context="property"
                                    errors={{}}
                                    disableInputs={true}
                                    onChange={(field, value) =>
                                        handleInput("property.propertyAttributes." + field, value)
                                    }
                                />
                            </React.Fragment>
                        )}
                        {toLower(propertyType) === "healthcare" && (
                            <React.Fragment>
                                <Divider />
                                <FormTitle title="Healthcare Information" />
                                <HealthcareForm
                                    {...props}
                                    data={get(property, "propertyAttributes", {})}
                                    context="property"
                                    errors={{}}
                                    disableInputs={true}
                                    onChange={(field, value) =>
                                        handleInput("property.propertyAttributes." + field, value)
                                    }
                                />
                            </React.Fragment>
                        )}
                        {toLower(propertyType) === "industrial" && (
                            <React.Fragment>
                                <Divider />
                                <FormTitle title="Industrial Information" />
                                <IndustrialForm
                                    {...props}
                                    data={get(property, "propertyAttributes", {})}
                                    context="property"
                                    errors={{ errors }}
                                    disableInputs={true}
                                    onChange={(field, value) =>
                                        handleInput("property.propertyAttributes." + field, value)
                                    }
                                />
                            </React.Fragment>
                        )}
                        {toLower(propertyType) === "retail" && (
                            <React.Fragment>
                                <Divider />
                                <FormTitle title="Retail Information" />
                                <RetailForm
                                    {...props}
                                    data={get(property, "propertyAttributes", {})}
                                    context="property"
                                    errors={{}}
                                    disableInputs={true}
                                    onChange={(field, value) =>
                                        handleInput("property.propertyAttributes." + field, value)
                                    }
                                />
                            </React.Fragment>
                        )}
                        {toLower(propertyType) === "land" && (
                            <React.Fragment>
                                <Divider />
                                <FormTitle title="Land Information" />
                                <LandForm
                                    {...props}
                                    data={get(property, "propertyAttributes", {})}
                                    context="property"
                                    errors={{}}
                                    disableInputs={true}
                                    onChange={(field, value) =>
                                        handleInput("property.propertyAttributes." + field, value)
                                    }
                                />
                            </React.Fragment>
                        )}
                        {toLower(propertyType) === "multifamily" && (
                            <React.Fragment>
                                <Divider />
                                <FormTitle title="Multifamily Information" />
                                <div>
                                    <MultifamilyForm
                                        {...props}
                                        data={get(property, "propertyAttributes", {})}
                                        context="property"
                                        errors={{}}
                                        disableInputs={true}
                                        onChange={(field, value) =>
                                            handleInput("property.propertyAttributes." + field, value)
                                        }
                                    />
                                </div>
                            </React.Fragment>
                        )}
                        {toLower(propertyType) === "hospitality" && (
                            <React.Fragment>
                                <Divider />
                                <FormTitle title="Hospitality Information" />
                                <div>
                                    <HospitalityForm
                                        {...props}
                                        data={get(property, "propertyAttributes", {})}
                                        context="property"
                                        errors={{}}
                                        disableInputs={true}
                                        onChange={(field, value) =>
                                            handleInput("property.propertyAttributes." + field, value)
                                        }
                                    />
                                </div>
                            </React.Fragment>
                        )}
                        {toLower(propertyType) === "other" && (
                            <React.Fragment>
                                <Divider />
                                <FormTitle title="Other Information" />
                                <div>
                                    <OtherForm
                                        {...props}
                                        data={get(property, "propertyAttributes", {})}
                                        context="property"
                                        errors={{}}
                                        disableInputs={true}
                                        onChange={(field, value) =>
                                            handleInput("property.propertyAttributes." + field, value)
                                        }
                                    />
                                </div>
                            </React.Fragment>
                        )}
                        <Divider />
                        <FormTitle title="Attached Documents" />
                        <DocumentDrop
                            entityKey={propertyKey}
                            entityStatus={property?.propertyAttributes?.propertyStatus}
                            type="property"
                            documentTypes="propertyDetail"
                            viewOnly={true}
                            parentType={"PROPERTY"}
                            stateCode={get(property, "primaryAddress.city.state.stateCode", null)}
                        />
                    </React.Fragment>
                )}

                {/*** Research ***/}

                {tab === "research_tab" && includes(["research", "dataEntry", "dataAdmin", "researchAdmin"], role) && (
                    <ResearchTab {...props} propertyKey={propertyKey} editResponse={handleEditResearchSnapshot} />
                )}
            </StyledPaperPadding>

            <Dialog
                open={closeDealDialogOpen}
                onClose={handleClose}
                aria-labelledby="close-deal"
                aria-describedby="close-deal-dialog"
            >
                <DialogTitle id="close-deal">{"Are you sure?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Closing this deal will start the invoice process for this transaction and move the listing to a
                        closed status. Portland Listings will be closed after the Comp for this Deal has been Submitted
                        and Approved.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary" variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleClose} color="secondary" autoFocus variant="contained">
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

export default PropertyDetail;
